import { type Item, parseLocalDate, type SnoozePreset, zonedToUtc } from '@hugo/core'
import { AlarmClockIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useItemActions } from '@/lib/item-actions'

const PRESETS: { preset: SnoozePreset; label: string }[] = [
  { preset: 'later_today', label: 'Later today' },
  { preset: 'tomorrow', label: 'Tomorrow' },
  { preset: 'in_2_days', label: 'In 2 days' },
  { preset: 'next_week', label: 'Next week' },
]

/** Snooze picker: presets (keys 1–4) or a date (09:00 in the user's time zone). */
export function SnoozeMenu({
  item,
  open,
  onOpenChange,
  timeZone,
  size = 'sm',
}: {
  item: Item | null
  open: boolean
  onOpenChange: (open: boolean) => void
  timeZone: string
  size?: 'sm' | 'default'
}) {
  const actions = useItemActions()
  const [date, setDate] = useState('')
  const choose = (preset: SnoozePreset) => {
    if (!item) return
    onOpenChange(false)
    void actions.snooze(item, preset)
  }
  const chooseDate = () => {
    if (!item || !date) return
    onOpenChange(false)
    const until = new Date(zonedToUtc(parseLocalDate(date), 9 * 60, timeZone)).toISOString()
    void actions.snoozeUntil(item, until, date)
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <Button variant="outline" size={size} disabled={!item} aria-label="Snooze (S)">
            <AlarmClockIcon aria-hidden="true" />
            Snooze
            <Kbd className="max-md:hidden">S</Kbd>
          </Button>
        }
      />
      <PopoverContent
        className="w-64 p-2"
        aria-label="Snooze until"
        onKeyDown={(event) => {
          const index = Number(event.key) - 1
          const choice = PRESETS[index]
          if (choice) {
            event.preventDefault()
            choose(choice.preset)
          }
        }}
      >
        <div className="flex flex-col gap-1">
          {PRESETS.map((p, i) => (
            <Button
              key={p.preset}
              variant="ghost"
              className="justify-between"
              onClick={() => choose(p.preset)}
            >
              {p.label}
              <Kbd>{i + 1}</Kbd>
            </Button>
          ))}
          <form
            className="mt-1 flex gap-1 border-t pt-2"
            onSubmit={(event) => {
              event.preventDefault()
              chooseDate()
            }}
          >
            <Input
              type="date"
              aria-label="Snooze until date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8"
            />
            <Button type="submit" size="sm" disabled={!date}>
              Set
            </Button>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  )
}
